import React, { useState, ChangeEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import loginbackground from "../shared/assets/background-images/backgroundimg.png";
import menubackground from "../shared/assets/background-images/overlayrimg.png";
import Rectangle from "../shared/assets/background-images/Rectangle.png";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import { FaEyeSlash, FaEye } from "react-icons/fa";
import { MdErrorOutline } from "react-icons/md";

// ✅ Custom Checkbox
const CustomCheckbox = styled((props: CheckboxProps) => (
  <Checkbox
    disableRipple
    color="default"
    icon={<span className="checkbox-icon" />}
    checkedIcon={<span className="checkbox-icon checked" />}
    {...props}
  />
))(() => ({
  "&:hover": { backgroundColor: "transparent" },
  "& .checkbox-icon": {
    width: 20,
    height: 20,
    borderRadius: 6,
    border: "2px solid #fb9c18",
    display: "inline-block",
    transition: "all 0.3s ease",
    backgroundColor: "transparent",
    position: "relative",
  },
  "& .checkbox-icon.checked": {
    backgroundColor: "#fb9c18",
    borderColor: "#fb9c18",
  },
  "& .checkbox-icon.checked::after": {
    content: '""',
    position: "absolute",
    left: "5px",
    top: "2px",
    width: "6px",
    height: "10px",
    border: "solid black",
    borderWidth: "0 2px 2px 0",
    transform: "rotate(45deg)",
  },
}));

// ✅ Animations
const outerVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeOut" } },
};

const logoVariants = {
  hidden: { opacity: 0, y: 20, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { delay: 0.6, duration: 0.8, ease: "easeOut" },
  },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { delayChildren: 1.4, staggerChildren: 0.25 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25, filter: "blur(10px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const LoginPage: React.FC = () => {
  const [checked, setChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [touched, setTouched] = useState<{ email?: boolean; password?: boolean }>({});

  const validateEmail = (value: string): string | undefined => {
    if (!value) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return undefined;
  };

  const validatePassword = (value: string): string | undefined => {
    if (!value) return "Password is required";
    if (value.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(value)) return "Password must contain one uppercase letter";
    if (!/[a-z]/.test(value)) return "Password must contain one lowercase letter";
    if (!/[0-9]/.test(value)) return "Password must contain one number";
    return undefined;
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) setErrors((p) => ({ ...p, email: validateEmail(value) }));
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) setErrors((p) => ({ ...p, password: validatePassword(value) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    setTouched({ email: true, password: true });
    setErrors({ email: emailError, password: passwordError });
    if (!emailError && !passwordError && checked) alert("Login successful!");
  };

  const emailValid = !validateEmail(email);
  const passwordValid = !validatePassword(password);
  const passwordFilled = password.length > 0;

  return (
    <div
      className="w-full h-screen flex items-center justify-center md:justify-end relative overflow-hidden"
      style={{
        backgroundImage: `url(${loginbackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <motion.div
        variants={outerVariants}
        initial="hidden"
        animate="visible"
        className="relative h-[96vh] w-[90%] m-4 sm:w-[70%] md:w-[55%] lg:w-[45%] xl:w-[40%] rounded-[30px] text-[#F9F9F9] z-10 overflow-hidden"
        style={{
          backgroundImage: `url(${menubackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="h-full flex flex-col items-center justify-center mt-2">
          <div className="w-[90%] sm:w-[85%] md:w-[87%]">

            {/* Logo */}
            <motion.div variants={logoVariants} initial="hidden" animate="visible" className="flex justify-start mb-4">
              <img src={Rectangle} alt="Logo" className="h-20" />
            </motion.div>

            {/* Welcome text */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-4">
              <motion.div variants={itemVariants} className="md:px-5">
                <h2 className="text-3xl font-bold">Welcome back!</h2>
                <p className="text-[#F9F9F9] text-md py-1">Log in to explore the possibilities</p>
              </motion.div>

              {/* FORM */}
              <motion.form onSubmit={handleSubmit} className="space-y-4 sm:px-12 px-3">

                {/* EMAIL */}
                <motion.div variants={itemVariants} className="px-2">
                  <label className="block mb-1">Email</label>
                  <input
                    type="text"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                    className={`w-full bg-transparent border rounded-lg px-3 py-2 border-[#f9f9f9] focus:border-[#fb9c18] focus:outline-none ${errors.email ? "border-red-500" : ""
                      }`}
                  />
                  {errors.email && (
                    <p className="flex items-center gap-1 text-red-500 text-sm mt-1">
                      <MdErrorOutline className="text-lg" />
                      {errors.email}
                    </p>
                  )}
                </motion.div>

                {/* PASSWORD + CHECKBOX + LOGIN BUTTON */}
                <AnimatePresence>
                  {emailValid && (
                    <motion.div
                      key="passwordBlock"
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      
                    >
                      {/* PASSWORD INPUT */}
                      <label className="block mb-1 px-2 mt-3">Password</label>
                      <div className="relative px-2">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={handlePasswordChange}
                          onBlur={() => setTouched((p) => ({ ...p, password: true }))}
                          className={`w-full bg-transparent border rounded-lg px-3 py-2 border-[#f9f9f9] focus:border-[#fb9c18] focus:outline-none pr-10 ${errors.password ? "border-red-500" : ""
                            }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute top-3.5 right-5 text-[#f9f9f9]"
                        >
                          {showPassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>

                      {errors.password && (
                        <p className="flex items-center gap-1 text-red-500 text-sm mt-1">
                          <MdErrorOutline className="text-lg" />
                          {errors.password}
                        </p>
                      )}

                      {/* CHECKBOX + FORGOT PASSWORD */}
                      <motion.div
                        key="actions"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={itemVariants}
                        className="flex justify-between flex-wrap sm:flex-nowrap items-center mt-3"

                      >
                        <div className="flex items-center">
                          <CustomCheckbox
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                          />
                          <span className="">Remember me</span>
                        </div>
                        <p className="text-[#fb9c18] underline cursor-pointer px-2">Forgot password?</p>
                      </motion.div>

                      <AnimatePresence>
                        {password.length > 0 && (
                          <motion.div
                            key="loginbtn"
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            className="mt-4"
                          >
                            <button
                              type="submit"
                              className="w-full bg-[#fb9c18] text-white py-3 rounded-lg font-semibold hover:bg-[#e38a15]"
                            >
                              Log in
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* CHECKBOX + FORGOT PASSWORD */}
                {!emailValid && (
                  <motion.div
                    key="actions"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={itemVariants}
                    className="flex justify-between flex-wrap sm:flex-nowrap items-center mt-3"
                  >
                    <div className="flex items-center">
                      <CustomCheckbox
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                      />
                      <span className="">Remember me</span>
                    </div>
                    <p className="text-[#fb9c18] px-1 underline cursor-pointer">Forgot password?</p>
                  </motion.div>
                )}


                {/* SIGN UP always visible */}
                <motion.p variants={itemVariants} className=" px-2 text-center sm:text-left">
                  Don’t have an account yet?{" "}
                  <a
                    href="/signup"
                    className="text-[#fb9c18] underline font-semibold hover:text-[#e38a15]"
                  >
                    Sign up here!
                  </a>
                </motion.p>
              </motion.form>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
